import CategoryScoreCardHero from './CategoryScoreCardHero';
import { DATA_SOURCES } from '@/config/constants';
import DownloadPopularityTimeSeriesButton from 'components/Button/DownloadPopularityTimeSeriesButton';
import { exportEntity, hasFlavorsTab } from '../shared/entityPredicates';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityChartDownload from '../shared/entity/EntityChartDownload';
import EntityFlavors from '../shared/entity/EntityFlavors';
import EntityGraphNav from '../shared/entity/EntityGraphNav';
import EntityPage from '../shared/entity/EntityPage';
import EntityRelatedTable from '../shared/entity/EntityRelatedTable';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTimeSeriesTab from '../shared/entity/EntityTimeSeriesTab';
import PopularityBreadCrumbs from 'components/Breadcrumbs/PopularityBreadCrumbs';
import PopularityEntityChart, {
    popularityDownloadSeries,
    popularitySeries,
} from '../shared/entity/PopularityEntityChart';
import PopularityGraphNav from 'components/DataVis/GraphNav/PopularityGraphNav';
import PopularityIndexRelatedEntitiesSectionTable from 'components/DataVis/PopularityIndexRelatedEntitiesTable/PopularityIndexRelatedEntitiesSectionTable';
import { POPULARITY_METADATA_BY_KEY } from 'utils/metrics';
import Stack from 'components/Stack';
import { SUMMARY_SPEC, scoreCardSummary } from './categorySummary';
import usePopIndexEntity, {
    usePopIndexSiblings,
} from '../shared/entity/usePopIndexEntity';
import { useCurrentCountry } from 'utils/hooks';
import useParentCategory from './useParentCategory';
import {
    useTrackCategoryView,
    useTrackDepthView,
} from 'utils/hooks/useTrackEntityView';
import { views } from '@/copy';

const { categoryThree } = views;

const HERO_METRICS = [
    POPULARITY_METADATA_BY_KEY.YoY,
    POPULARITY_METADATA_BY_KEY.QoQ,
    POPULARITY_METADATA_BY_KEY.MoM,
];

function PopularityCategoryThreePage({ categoryId, controller, tabs }) {
    const country = useCurrentCountry();
    const page = usePopIndexEntity({
        entityType: 'categories',
        entityId: categoryId,
        country,
        controller,
        coverage: 'hint',
    });
    const { entity: category, details, row, name, rowQuery } = page;

    const parentCategory = useParentCategory(page);
    const { siblings, ready } = usePopIndexSiblings({
        parentCategory,
        country,
    });
    const volume = row?.popularity_index_avg_12_month;

    useTrackCategoryView(DATA_SOURCES.POPULARITY, {
        id: categoryId,
        categoryName: name,
        countryName: country?.name || null,
    });
    useTrackDepthView(DATA_SOURCES.POPULARITY, {
        depth: details?.depth,
        name,
        country,
    });

    const series = popularitySeries({ page });

    const summary = scoreCardSummary({
        entity: category,
        parentCategory,
        siblings,
        ready,
        spec: SUMMARY_SPEC[DATA_SOURCES.POPULARITY],
        currentPage: {
            id: categoryId,
            name,
            volume,
            absoluteGrowth: row?.increase_YoY,
            rank: row?.rank || null,
        },
    });

    return (
        <EntityPage
            page={page}
            breadcrumbs={<EntityBreadcrumbs as={PopularityBreadCrumbs} />}
            sourceTabs={tabs}
            space="32px"
            tabs={
                <EntityTabs>
                    <EntityTab name="related categories">
                        <EntityRelatedTable
                            as={PopularityIndexRelatedEntitiesSectionTable}
                            subject="categories"
                            icon="category"
                            title="Related Categories"
                            exportType="related-category"
                            exportEntity={exportEntity(page)}
                            sortParamKey="categoriesSort"
                            paginateBy="categories"
                            parented
                        />
                    </EntityTab>
                    <EntityTab name="brands">
                        <EntityRelatedTable
                            as={PopularityIndexRelatedEntitiesSectionTable}
                            subject="brands"
                            icon="brand"
                            title="Brands"
                            exportType="brand"
                            exportEntity={exportEntity(page)}
                            sortParamKey="brandsSort"
                            paginateBy="brands"
                            parented
                            showAddToWatchlist={false}
                        />
                    </EntityTab>
                    {hasFlavorsTab(page) && (
                        <EntityTab name="Flavors">
                            <EntityFlavors
                                dataSource="pop_index"
                                exportEntity={exportEntity(page)}
                                parented
                                tooltipKey="flavorsTitleHelpPopIndex"
                            />
                        </EntityTab>
                    )}
                    <EntityTab name="Prediction">
                        <EntityTimeSeriesTab
                            title={categoryThree.sections.prediction.title}
                            subtitle={
                                categoryThree.sections.prediction.subtitle
                            }
                            tooltipKey="predictionTitleHelp"
                            refOnWrapper
                            download={
                                <EntityChartDownload
                                    as={DownloadPopularityTimeSeriesButton}
                                    data={popularityDownloadSeries({ page })}
                                    exportEntity={name}
                                    className="ml-auto"
                                />
                            }
                        >
                            <PopularityEntityChart
                                data={series}
                                combined={false}
                                prediction
                                name={name}
                                categoryDepth={details?.depth}
                            />
                        </EntityTimeSeriesTab>
                    </EntityTab>
                </EntityTabs>
            }
        >
            <Stack $space="16px">
                <CategoryScoreCardHero
                    categoryId={categoryId}
                    name={name}
                    image={details?.image}
                    isLoading={rowQuery.isLoading}
                    volumeLabel="Popularity"
                    volume={volume}
                    metrics={row}
                    metricsList={HERO_METRICS}
                    defaultMetric={POPULARITY_METADATA_BY_KEY.YoY}
                    cluster={row?.cluster}
                    {...summary}
                />
                <EntityGraphNav as={PopularityGraphNav} exportEntity={name} />
            </Stack>
        </EntityPage>
    );
}

PopularityCategoryThreePage.displayName = 'PopularityCategoryThree';

export default PopularityCategoryThreePage;
